import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Chip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as RunIcon,
  Add as AddIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  DragIndicator as DragIcon,
  Navigation as NavIcon,
  Keyboard as TypeIcon,
  TouchApp as ClickIcon,
  HourglassEmpty as WaitIcon,
  CheckCircle as AssertIcon,
  SelectAll as SelectActionIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// ---------- Step type definitions ----------

const STEP_TEMPLATES = {
  'action.navigate': {
    label: 'Navigate',
    type: 'action',
    inputData: { action: 'navigate' },
    defaultName: 'Navigate to URL',
  },
  'action.type': {
    label: 'Type Text',
    type: 'action',
    inputData: { action: 'type', selector: '', text: '', clearFirst: true },
    defaultName: 'Type into field',
  },
  'action.click': {
    label: 'Click Element',
    type: 'action',
    inputData: { action: 'click', selector: '' },
    defaultName: 'Click element',
  },
  'action.select': {
    label: 'Select Option',
    type: 'action',
    inputData: { action: 'select', selector: '', value: '' },
    defaultName: 'Select option',
  },
  'wait.selector': {
    label: 'Wait for Selector',
    type: 'wait',
    inputData: { waitType: 'selector', selector: '', timeout: 10000 },
    defaultName: 'Wait for element',
  },
  'wait.navigation': {
    label: 'Wait for Navigation',
    type: 'wait',
    inputData: { waitType: 'navigation', timeout: 15000 },
    defaultName: 'Wait for navigation',
  },
  'wait.time': {
    label: 'Wait (delay)',
    type: 'wait',
    inputData: { waitType: 'time', timeout: 2000 },
    defaultName: 'Wait for delay',
  },
  'assertion.url': {
    label: 'Assert URL',
    type: 'assertion',
    inputData: { assertionType: 'url', operator: 'not_contains', value: '' },
    defaultName: 'Assert URL condition',
  },
  'assertion.element': {
    label: 'Assert Element',
    type: 'assertion',
    inputData: { assertionType: 'element', selector: '', exists: true },
    defaultName: 'Assert element exists',
  },
  'assertion.text': {
    label: 'Assert Text',
    type: 'assertion',
    inputData: { assertionType: 'text', selector: '', operator: 'contains', value: '' },
    defaultName: 'Assert text content',
  },
};

const STEP_TYPE_ITEMS = Object.entries(STEP_TEMPLATES).map(([key, tmpl]) => ({
  value: key,
  label: tmpl.label,
  type: tmpl.type,
}));

// ---------- Helpers ----------

const getStepTypeKey = (step) => {
  if (step.type === 'action') return `action.${step.inputData?.action || 'navigate'}`;
  if (step.type === 'wait') return `wait.${step.inputData?.waitType || 'selector'}`;
  if (step.type === 'assertion') return `assertion.${step.inputData?.assertionType || 'url'}`;
  return 'action.navigate';
};

const TYPE_COLORS = {
  action: { bg: '#e3f2fd', border: '#1976d2', text: '#1565c0', chip: 'primary' },
  wait: { bg: '#fff8e1', border: '#f9a825', text: '#f57f17', chip: 'warning' },
  assertion: { bg: '#e8f5e9', border: '#2e7d32', text: '#1b5e20', chip: 'success' },
};

const TYPE_ICONS = {
  'action.navigate': <NavIcon fontSize="small" />,
  'action.type': <TypeIcon fontSize="small" />,
  'action.click': <ClickIcon fontSize="small" />,
  'action.select': <SelectActionIcon fontSize="small" />,
  'wait.selector': <WaitIcon fontSize="small" />,
  'wait.navigation': <WaitIcon fontSize="small" />,
  'wait.time': <WaitIcon fontSize="small" />,
  'assertion.url': <AssertIcon fontSize="small" />,
  'assertion.element': <AssertIcon fontSize="small" />,
  'assertion.text': <AssertIcon fontSize="small" />,
};

/** Generate a summary line for a step */
const getStepSummary = (step) => {
  const d = step.inputData || {};
  switch (step.type) {
    case 'action':
      switch (d.action) {
        case 'navigate':
          return d.url ? `Navigate to ${d.url}` : 'Navigate (no URL set)';
        case 'type':
          return `Type "${d.text || '...'}" into ${d.selector || '...'}${d.clearFirst ? ' (clear first)' : ''}`;
        case 'click':
          return `Click ${d.selector || '...'}`;
        case 'select':
          return `Select "${d.value || '...'}" in ${d.selector || '...'}`;
        default:
          return `Action: ${d.action || 'unknown'}`;
      }
    case 'wait':
      switch (d.waitType) {
        case 'selector':
          return `Wait for ${d.selector || '...'} (${d.timeout || 10000}ms)`;
        case 'navigation':
          return `Wait for navigation (${d.timeout || 15000}ms)`;
        case 'time':
          return `Wait ${d.timeout || 2000}ms`;
        default:
          return `Wait: ${d.waitType || 'unknown'}`;
      }
    case 'assertion':
      switch (d.assertionType) {
        case 'url':
          return `Assert URL ${(d.operator || 'contains').replace('_', ' ')} "${d.value || '...'}"`;
        case 'element':
          return `Assert ${d.selector || '...'} ${d.exists !== false ? 'exists' : 'does not exist'}`;
        case 'text':
          return `Assert text in ${d.selector || '...'} ${(d.operator || 'contains').replace('_', ' ')} "${d.value || '...'}"`;
        default:
          return `Assert: ${d.assertionType || 'unknown'}`;
      }
    default:
      return step.name || 'Unknown step';
  }
};

// ---------- Credential placeholder buttons ----------

const CredentialHelpers = ({ onInsert }) => (
  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
    <Chip
      label="{{credential.username}}"
      size="small"
      variant="outlined"
      onClick={() => onInsert('{{credential.username}}')}
      sx={{ cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.7rem' }}
    />
    <Chip
      label="{{credential.password}}"
      size="small"
      variant="outlined"
      onClick={() => onInsert('{{credential.password}}')}
      sx={{ cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.7rem' }}
    />
  </Box>
);

// ---------- Inline step editor fields ----------

const StepFields = ({ step, onChange }) => {
  const d = step.inputData || {};

  const updateInput = (field, value) => {
    onChange({
      ...step,
      inputData: { ...d, [field]: value },
    });
  };

  const fields = [];

  // Name field (always)
  fields.push(
    <TextField
      key="name"
      fullWidth
      size="small"
      label="Step Name"
      value={step.name || ''}
      onChange={(e) => onChange({ ...step, name: e.target.value })}
      sx={{ mb: 1.5 }}
    />
  );

  switch (step.type) {
    case 'action':
      switch (d.action) {
        case 'navigate':
          fields.push(
            <TextField
              key="url"
              fullWidth
              size="small"
              label="URL"
              value={d.url || ''}
              onChange={(e) => updateInput('url', e.target.value)}
              placeholder="https://example.com/login"
              sx={{ mb: 1 }}
            />
          );
          break;
        case 'type':
          fields.push(
            <TextField
              key="selector"
              fullWidth
              size="small"
              label="Selector"
              value={d.selector || ''}
              onChange={(e) => updateInput('selector', e.target.value)}
              placeholder="input#username"
              sx={{ mb: 1 }}
              InputProps={{ sx: { fontFamily: 'monospace' } }}
            />
          );
          fields.push(
            <Box key="text-box" sx={{ mb: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="Text / Value"
                value={d.text || ''}
                onChange={(e) => updateInput('text', e.target.value)}
                placeholder="{{credential.username}}"
                InputProps={{ sx: { fontFamily: 'monospace' } }}
              />
              <CredentialHelpers onInsert={(v) => updateInput('text', v)} />
            </Box>
          );
          fields.push(
            <FormControlLabel
              key="clear"
              control={
                <Checkbox
                  checked={d.clearFirst !== false}
                  onChange={(e) => updateInput('clearFirst', e.target.checked)}
                  size="small"
                />
              }
              label="Clear field first"
            />
          );
          break;
        case 'click':
          fields.push(
            <TextField
              key="selector"
              fullWidth
              size="small"
              label="Selector"
              value={d.selector || ''}
              onChange={(e) => updateInput('selector', e.target.value)}
              placeholder="button#login"
              sx={{ mb: 1 }}
              InputProps={{ sx: { fontFamily: 'monospace' } }}
            />
          );
          break;
        case 'select':
          fields.push(
            <TextField
              key="selector"
              fullWidth
              size="small"
              label="Selector"
              value={d.selector || ''}
              onChange={(e) => updateInput('selector', e.target.value)}
              placeholder="select#role"
              sx={{ mb: 1 }}
              InputProps={{ sx: { fontFamily: 'monospace' } }}
            />
          );
          fields.push(
            <TextField
              key="value"
              fullWidth
              size="small"
              label="Option Value"
              value={d.value || ''}
              onChange={(e) => updateInput('value', e.target.value)}
              sx={{ mb: 1 }}
            />
          );
          break;
        default:
          break;
      }
      break;

    case 'wait':
      if (d.waitType === 'selector') {
        fields.push(
          <TextField
            key="selector"
            fullWidth
            size="small"
            label="Selector"
            value={d.selector || ''}
            onChange={(e) => updateInput('selector', e.target.value)}
            placeholder="input#username"
            sx={{ mb: 1 }}
            InputProps={{ sx: { fontFamily: 'monospace' } }}
          />
        );
      }
      fields.push(
        <TextField
          key="timeout"
          size="small"
          label="Timeout (ms)"
          type="number"
          value={d.timeout || ''}
          onChange={(e) => updateInput('timeout', parseInt(e.target.value, 10) || 0)}
          sx={{ mb: 1, width: 180 }}
        />
      );
      break;

    case 'assertion':
      switch (d.assertionType) {
        case 'url':
          fields.push(
            <FormControl key="operator" size="small" sx={{ mb: 1, minWidth: 200 }}>
              <InputLabel>Operator</InputLabel>
              <Select
                value={d.operator || 'contains'}
                label="Operator"
                onChange={(e) => updateInput('operator', e.target.value)}
              >
                <MenuItem value="contains">contains</MenuItem>
                <MenuItem value="not_contains">not contains</MenuItem>
                <MenuItem value="equals">equals</MenuItem>
              </Select>
            </FormControl>
          );
          fields.push(
            <TextField
              key="value"
              fullWidth
              size="small"
              label="Value"
              value={d.value || ''}
              onChange={(e) => updateInput('value', e.target.value)}
              placeholder="/auth/login"
              sx={{ mb: 1 }}
              InputProps={{ sx: { fontFamily: 'monospace' } }}
            />
          );
          break;
        case 'element':
          fields.push(
            <TextField
              key="selector"
              fullWidth
              size="small"
              label="Selector"
              value={d.selector || ''}
              onChange={(e) => updateInput('selector', e.target.value)}
              sx={{ mb: 1 }}
              InputProps={{ sx: { fontFamily: 'monospace' } }}
            />
          );
          fields.push(
            <FormControlLabel
              key="exists"
              control={
                <Checkbox
                  checked={d.exists !== false}
                  onChange={(e) => updateInput('exists', e.target.checked)}
                  size="small"
                />
              }
              label="Element should exist"
            />
          );
          break;
        case 'text':
          fields.push(
            <TextField
              key="selector"
              fullWidth
              size="small"
              label="Selector"
              value={d.selector || ''}
              onChange={(e) => updateInput('selector', e.target.value)}
              sx={{ mb: 1 }}
              InputProps={{ sx: { fontFamily: 'monospace' } }}
            />
          );
          fields.push(
            <FormControl key="operator" size="small" sx={{ mb: 1, minWidth: 200 }}>
              <InputLabel>Operator</InputLabel>
              <Select
                value={d.operator || 'contains'}
                label="Operator"
                onChange={(e) => updateInput('operator', e.target.value)}
              >
                <MenuItem value="contains">contains</MenuItem>
                <MenuItem value="not_contains">not contains</MenuItem>
                <MenuItem value="equals">equals</MenuItem>
              </Select>
            </FormControl>
          );
          fields.push(
            <TextField
              key="value"
              fullWidth
              size="small"
              label="Value"
              value={d.value || ''}
              onChange={(e) => updateInput('value', e.target.value)}
              sx={{ mb: 1 }}
            />
          );
          break;
        default:
          break;
      }
      break;
    default:
      break;
  }

  return <Box>{fields}</Box>;
};

// ---------- Single Step Row ----------

const StepRow = ({ step, index, total, expanded, onToggleExpand, onChange, onDelete, onMoveUp, onMoveDown, onRunStep, dragHandleProps }) => {
  const typeKey = getStepTypeKey(step);
  const colors = TYPE_COLORS[step.type] || TYPE_COLORS.action;
  const icon = TYPE_ICONS[typeKey] || <NavIcon fontSize="small" />;
  const templateLabel = STEP_TEMPLATES[typeKey]?.label || step.type;

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 1,
        borderLeft: `4px solid ${colors.border}`,
        bgcolor: expanded ? colors.bg : 'transparent',
        transition: 'background-color 0.2s',
      }}
    >
      {/* Summary row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          py: 1,
          gap: 1,
        }}
      >
        {/* Drag handle */}
        <Box {...dragHandleProps} sx={{ cursor: 'grab', display: 'flex', color: 'text.disabled' }}>
          <DragIcon fontSize="small" />
        </Box>

        {/* Step number */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: colors.text,
            minWidth: 28,
            textAlign: 'center',
          }}
        >
          {index + 1}
        </Typography>

        {/* Type chip */}
        <Chip
          icon={icon}
          label={templateLabel}
          size="small"
          color={colors.chip}
          variant="outlined"
          sx={{ fontWeight: 600, fontSize: '0.75rem', minWidth: 100 }}
        />

        {/* Summary text */}
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: 'text.secondary',
            fontFamily: 'monospace',
            fontSize: '0.8rem',
          }}
        >
          {getStepSummary(step)}
        </Typography>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
          <Tooltip title="Move up">
            <span>
              <IconButton size="small" onClick={onMoveUp} disabled={index === 0}>
                <UpIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move down">
            <span>
              <IconButton size="small" onClick={onMoveDown} disabled={index === total - 1}>
                <DownIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={expanded ? 'Collapse' : 'Edit'}>
            <IconButton size="small" onClick={onToggleExpand} color={expanded ? 'primary' : 'default'}>
              {expanded ? <CollapseIcon fontSize="small" /> : <EditIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          {onRunStep && (
            <Tooltip title="Run this step">
              <IconButton size="small" color="primary" onClick={onRunStep}>
                <RunIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Duplicate">
            <IconButton size="small" onClick={() => onChange('duplicate')}>
              <CopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete step">
            <IconButton size="small" color="error" onClick={onDelete}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Expanded editor */}
      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ px: 2, py: 1.5, bgcolor: colors.bg }}>
          <StepFields step={step} onChange={onChange} />
        </Box>
      </Collapse>
    </Paper>
  );
};

// ---------- Add Step Dialog ----------

const AddStepDialog = ({ open, onClose, onAdd }) => {
  const [templateKey, setTemplateKey] = useState('action.navigate');
  const [name, setName] = useState('');

  const handleAdd = () => {
    const tmpl = STEP_TEMPLATES[templateKey];
    if (!tmpl) return;
    const step = {
      stepNumber: 0, // will be set by parent
      type: tmpl.type,
      name: name || tmpl.defaultName,
      inputData: { ...tmpl.inputData },
    };
    if (tmpl.inputData.action === 'navigate') {
      step.url = '';
    }
    onAdd(step);
    setName('');
    setTemplateKey('action.navigate');
    onClose();
  };

  const selectedTmpl = STEP_TEMPLATES[templateKey];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add Step</DialogTitle>
      <DialogContent>
        <FormControl fullWidth size="small" sx={{ mt: 1, mb: 2 }}>
          <InputLabel>Step Type</InputLabel>
          <Select
            value={templateKey}
            label="Step Type"
            onChange={(e) => {
              setTemplateKey(e.target.value);
              const t = STEP_TEMPLATES[e.target.value];
              if (t) setName(t.defaultName);
            }}
          >
            <MenuItem disabled sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'primary.main' }}>
              -- Actions --
            </MenuItem>
            {STEP_TYPE_ITEMS.filter((i) => i.type === 'action').map((i) => (
              <MenuItem key={i.value} value={i.value}>{i.label}</MenuItem>
            ))}
            <MenuItem disabled sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'warning.main' }}>
              -- Waits --
            </MenuItem>
            {STEP_TYPE_ITEMS.filter((i) => i.type === 'wait').map((i) => (
              <MenuItem key={i.value} value={i.value}>{i.label}</MenuItem>
            ))}
            <MenuItem disabled sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'success.main' }}>
              -- Assertions --
            </MenuItem>
            {STEP_TYPE_ITEMS.filter((i) => i.type === 'assertion').map((i) => (
              <MenuItem key={i.value} value={i.value}>{i.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          size="small"
          label="Step Name"
          value={name || selectedTmpl?.defaultName || ''}
          onChange={(e) => setName(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleAdd}>Add Step</Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------- Main StepEditor Component ----------

const StepEditor = ({ steps, onChange, onRunStep, onRunAll, onSave }) => {
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const currentSteps = Array.isArray(steps) ? steps : [];

  const renumber = (arr) =>
    arr.map((s, i) => ({ ...s, stepNumber: i + 1 }));

  const handleStepChange = useCallback(
    (index, updatedStep) => {
      const next = [...currentSteps];
      if (updatedStep === 'duplicate') {
        // Duplicate this step right after
        const clone = JSON.parse(JSON.stringify(next[index]));
        clone.name = (clone.name || '') + ' (copy)';
        next.splice(index + 1, 0, clone);
        onChange(renumber(next));
        setExpandedIndex(index + 1);
        return;
      }
      next[index] = updatedStep;
      onChange(renumber(next));
    },
    [currentSteps, onChange]
  );

  const handleDelete = useCallback(
    (index) => {
      const next = currentSteps.filter((_, i) => i !== index);
      onChange(renumber(next));
      if (expandedIndex === index) setExpandedIndex(null);
    },
    [currentSteps, onChange, expandedIndex]
  );

  const handleMove = useCallback(
    (index, direction) => {
      const next = [...currentSteps];
      const target = index + direction;
      if (target < 0 || target >= next.length) return;
      [next[index], next[target]] = [next[target], next[index]];
      onChange(renumber(next));
      if (expandedIndex === index) setExpandedIndex(target);
      else if (expandedIndex === target) setExpandedIndex(index);
    },
    [currentSteps, onChange, expandedIndex]
  );

  const handleAddStep = useCallback(
    (step) => {
      const next = [...currentSteps, step];
      onChange(renumber(next));
      setExpandedIndex(next.length - 1);
    },
    [currentSteps, onChange]
  );

  const handleDragEnd = useCallback(
    (result) => {
      if (!result.destination) return;
      const from = result.source.index;
      const to = result.destination.index;
      if (from === to) return;
      const next = [...currentSteps];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      onChange(renumber(next));
      if (expandedIndex === from) setExpandedIndex(to);
    },
    [currentSteps, onChange, expandedIndex]
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          Automation Steps ({currentSteps.length})
        </Typography>
        <Chip
          label="Action"
          size="small"
          color="primary"
          variant="outlined"
          sx={{ fontSize: '0.7rem', height: 22 }}
        />
        <Chip
          label="Wait"
          size="small"
          color="warning"
          variant="outlined"
          sx={{ fontSize: '0.7rem', height: 22 }}
        />
        <Chip
          label="Assertion"
          size="small"
          color="success"
          variant="outlined"
          sx={{ fontSize: '0.7rem', height: 22 }}
        />
      </Box>

      {/* Steps list */}
      {currentSteps.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            textAlign: 'center',
            mb: 2,
            borderStyle: 'dashed',
          }}
        >
          <Typography color="text.secondary" variant="body2">
            No steps defined. Click "Add Step" to get started.
          </Typography>
        </Paper>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="steps-list">
            {(provided) => (
              <Box ref={provided.innerRef} {...provided.droppableProps}>
                {currentSteps.map((step, index) => (
                  <Draggable key={`step-${index}`} draggableId={`step-${index}`} index={index}>
                    {(dragProvided) => (
                      <Box ref={dragProvided.innerRef} {...dragProvided.draggableProps}>
                        <StepRow
                          step={step}
                          index={index}
                          total={currentSteps.length}
                          expanded={expandedIndex === index}
                          onToggleExpand={() =>
                            setExpandedIndex(expandedIndex === index ? null : index)
                          }
                          onChange={(updated) => handleStepChange(index, updated)}
                          onDelete={() => handleDelete(index)}
                          onMoveUp={() => handleMove(index, -1)}
                          onMoveDown={() => handleMove(index, 1)}
                          onRunStep={onRunStep ? () => onRunStep(step, index) : null}
                          dragHandleProps={dragProvided.dragHandleProps}
                        />
                      </Box>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {/* Bottom actions */}
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
        >
          Add Step
        </Button>
        {onRunAll && currentSteps.length > 0 && (
          <Button
            variant="outlined"
            size="small"
            color="primary"
            startIcon={<RunIcon />}
            onClick={onRunAll}
          >
            Run All
          </Button>
        )}
        {onSave && (
          <Button
            variant="contained"
            size="small"
            onClick={onSave}
            sx={{ ml: 'auto' }}
          >
            Save Steps
          </Button>
        )}
      </Box>

      {/* Add step dialog */}
      <AddStepDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdd={handleAddStep}
      />
    </Box>
  );
};

// ---------- compileStepsToJs ----------

/**
 * Compiles an AutomationStep[] array into a raw JavaScript IIFE string.
 * The generated code is a self-contained content script that the extension can inject.
 */
export function compileStepsToJs(steps) {
  if (!Array.isArray(steps) || steps.length === 0) {
    return '// No steps defined\n';
  }

  const lines = [];
  lines.push('(async () => {');
  lines.push('  // --- Helper functions ---');
  lines.push('  const sleep = (ms) => new Promise(r => setTimeout(r, ms));');
  lines.push('');
  lines.push('  const setNativeValue = (el, value) => {');
  lines.push('    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(');
  lines.push("      window.HTMLInputElement.prototype, 'value'");
  lines.push('    )?.set;');
  lines.push('    if (nativeInputValueSetter) {');
  lines.push('      nativeInputValueSetter.call(el, value);');
  lines.push('    } else {');
  lines.push('      el.value = value;');
  lines.push('    }');
  lines.push("    el.dispatchEvent(new Event('input', { bubbles: true }));");
  lines.push("    el.dispatchEvent(new Event('change', { bubbles: true }));");
  lines.push('  };');
  lines.push('');
  lines.push('  const waitForSelector = (selector, timeout = 10000) => {');
  lines.push('    return new Promise((resolve, reject) => {');
  lines.push('      const el = document.querySelector(selector);');
  lines.push('      if (el) return resolve(el);');
  lines.push('      const observer = new MutationObserver(() => {');
  lines.push('        const el = document.querySelector(selector);');
  lines.push('        if (el) { observer.disconnect(); resolve(el); }');
  lines.push('      });');
  lines.push('      observer.observe(document.body, { childList: true, subtree: true });');
  lines.push('      setTimeout(() => {');
  lines.push('        observer.disconnect();');
  lines.push('        reject(new Error(`Timeout waiting for ${selector}`));');
  lines.push('      }, timeout);');
  lines.push('    });');
  lines.push('  };');
  lines.push('');
  lines.push('  const waitForNavigation = (timeout = 15000) => {');
  lines.push('    return new Promise((resolve) => {');
  lines.push('      const startUrl = window.location.href;');
  lines.push('      const check = () => {');
  lines.push('        if (window.location.href !== startUrl) return resolve();');
  lines.push('        setTimeout(check, 200);');
  lines.push('      };');
  lines.push('      setTimeout(check, 200);');
  lines.push('      setTimeout(resolve, timeout);');
  lines.push('    });');
  lines.push('  };');
  lines.push('');
  lines.push('  try {');

  for (const step of steps) {
    const d = step.inputData || {};
    const comment = `    // Step ${step.stepNumber}: ${step.name || ''}`;
    lines.push(comment);

    switch (step.type) {
      case 'action':
        switch (d.action) {
          case 'navigate':
            lines.push(`    window.location.href = ${JSON.stringify(d.url || '')};`);
            lines.push('    await sleep(2000);');
            break;
          case 'type': {
            const sel = JSON.stringify(d.selector || '');
            const text = JSON.stringify(d.text || '');
            lines.push(`    {`);
            lines.push(`      const el = await waitForSelector(${sel});`);
            if (d.clearFirst !== false) {
              lines.push(`      el.focus();`);
              lines.push(`      el.value = '';`);
            }
            lines.push(`      setNativeValue(el, ${text});`);
            lines.push(`    }`);
            break;
          }
          case 'click': {
            const sel = JSON.stringify(d.selector || '');
            lines.push(`    {`);
            lines.push(`      const el = await waitForSelector(${sel});`);
            lines.push(`      el.click();`);
            lines.push(`    }`);
            break;
          }
          case 'select': {
            const sel = JSON.stringify(d.selector || '');
            const val = JSON.stringify(d.value || '');
            lines.push(`    {`);
            lines.push(`      const el = await waitForSelector(${sel});`);
            lines.push(`      setNativeValue(el, ${val});`);
            lines.push(`    }`);
            break;
          }
          default:
            lines.push(`    // Unknown action: ${d.action}`);
        }
        break;

      case 'wait':
        switch (d.waitType) {
          case 'selector':
            lines.push(`    await waitForSelector(${JSON.stringify(d.selector || '')}, ${d.timeout || 10000});`);
            break;
          case 'navigation':
            lines.push(`    await waitForNavigation(${d.timeout || 15000});`);
            break;
          case 'time':
            lines.push(`    await sleep(${d.timeout || 2000});`);
            break;
          default:
            lines.push(`    // Unknown wait type: ${d.waitType}`);
        }
        break;

      case 'assertion':
        switch (d.assertionType) {
          case 'url': {
            const val = JSON.stringify(d.value || '');
            if (d.operator === 'equals') {
              lines.push(`    if (window.location.href !== ${val}) throw new Error('URL assertion failed: expected ' + ${val});`);
            } else if (d.operator === 'not_contains') {
              lines.push(`    if (window.location.href.includes(${val})) throw new Error('URL assertion failed: should not contain ' + ${val});`);
            } else {
              lines.push(`    if (!window.location.href.includes(${val})) throw new Error('URL assertion failed: should contain ' + ${val});`);
            }
            break;
          }
          case 'element': {
            const sel = JSON.stringify(d.selector || '');
            if (d.exists !== false) {
              lines.push(`    if (!document.querySelector(${sel})) throw new Error('Element assertion failed: ' + ${sel} + ' not found');`);
            } else {
              lines.push(`    if (document.querySelector(${sel})) throw new Error('Element assertion failed: ' + ${sel} + ' should not exist');`);
            }
            break;
          }
          case 'text': {
            const sel = JSON.stringify(d.selector || '');
            const val = JSON.stringify(d.value || '');
            lines.push(`    {`);
            lines.push(`      const el = document.querySelector(${sel});`);
            lines.push(`      const txt = el ? el.textContent : '';`);
            if (d.operator === 'equals') {
              lines.push(`      if (txt !== ${val}) throw new Error('Text assertion failed');`);
            } else if (d.operator === 'not_contains') {
              lines.push(`      if (txt.includes(${val})) throw new Error('Text assertion failed');`);
            } else {
              lines.push(`      if (!txt.includes(${val})) throw new Error('Text assertion failed');`);
            }
            lines.push(`    }`);
            break;
          }
          default:
            lines.push(`    // Unknown assertion type: ${d.assertionType}`);
        }
        break;

      default:
        lines.push(`    // Unknown step type: ${step.type}`);
    }
    lines.push('');
  }

  lines.push("    console.log('[Automatrix] All steps completed successfully');");
  lines.push('  } catch (err) {');
  lines.push("    console.error('[Automatrix] Step execution failed:', err);");
  lines.push('    throw err;');
  lines.push('  }');
  lines.push('})();');

  return lines.join('\n');
}

export default StepEditor;
